/**
 * Spot checkpoint gallery photos in R2 (same COVERS bucket as thumbnails).
 */
import type { CoverExt } from "./quest-cover.ts";
import { COVER_CONTENT_TYPE } from "./quest-cover.ts";

const FILE = /^([0-9a-f]{32})\.(webp|jpg)$/;

export const MAX_SPOT_PHOTOS = 16;

/** R2 key: spot-photos/<missionId>/<seq>/<slot>/<hash>.webp */
export function spotPhotoKey(missionId: string, seq: number, slot: number, hash: string, ext: CoverExt): string {
  return `spot-photos/${missionId}/${seq}/${slot}/${hash}.${ext}`;
}

export function spotPhotoUrl(
  missionId: string,
  seq: number,
  slot: number,
  key: string | null | undefined,
): string | null {
  if (!key) return null;
  const prefix = `spot-photos/${missionId}/${seq}/${slot}/`;
  if (!key.startsWith(prefix)) return null;
  const file = key.slice(prefix.length);
  if (!FILE.test(file)) return null;
  return `/api/spot-photos/${encodeURIComponent(missionId)}/${seq}/${slot}/${file}`;
}

export function parseSpotPhotoFile(file: string): { hash: string; ext: CoverExt } | null {
  const m = FILE.exec(file);
  return m ? { hash: m[1]!, ext: m[2] as CoverExt } : null;
}

/** Reverse {@link spotPhotoUrl} for editor dirty snapshots (R2 key or null). */
export function spotPhotoKeyFromImageUrl(
  missionId: string,
  seq: number,
  slot: number,
  url: string | null | undefined,
): string | null {
  if (!url?.trim()) return null;
  let path: string;
  if (url.startsWith("http")) {
    path = new URL(url).pathname;
  } else {
    path = url.split("?")[0] ?? url;
  }
  const prefix = `/api/spot-photos/${encodeURIComponent(missionId)}/${seq}/${slot}/`;
  if (!path.startsWith(prefix)) return null;
  const file = path.slice(prefix.length);
  const parsed = parseSpotPhotoFile(file);
  if (!parsed) return null;
  return spotPhotoKey(missionId, seq, slot, parsed.hash, parsed.ext);
}

export { COVER_CONTENT_TYPE as SPOT_PHOTO_CONTENT_TYPE };
